import { useEffect, useRef, type MutableRefObject } from 'react';

/**
 * A ref that is `true` while the component is mounted and `false` once it is
 * not — for a deferred callback that must notice the component went away before
 * it fires (a save parked in `waitForProStudioIdle`, say).
 *
 * The arming happens in the effect's SETUP, not only in `useRef(true)`, and that
 * is the whole point. React 18 replays a mount as setup → cleanup → setup under
 * `StrictMode`, which `main.tsx` applies unconditionally, so a cleanup-only ref
 * is left `false` for the component's entire life in the dev build — every
 * guarded action then aborts, silently.
 *
 * CI is green against this class by construction, and that is worth knowing
 * before trusting a green run: `playwright.config.ts` declares no `webServer`,
 * so the suite hits whatever `FE_PRO_STUDIO_URL` points at — in CI a deployed
 * PRODUCTION build, where React does not double-invoke. It surfaced only from a
 * LOCAL run against the dev server on :5174, as a `waitForResponse` timeout.
 * So the proof of this hook has to be a unit test under `StrictMode` (with a
 * non-replaying control), not an e2e.
 *
 * `panelMountedRef` in `expanded-cell-view-panel.tsx` (PSTUDIO-1433) is the same
 * trap reached by a different fix: it starts `useRef(false)` and arms in setup,
 * so it was never exposed to the replay in the first place.
 */
export const useIsMountedRef = (): MutableRefObject<boolean> => {
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return isMountedRef;
};
